import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KoobAlert, KoobButton, KoobPill, icons } from '@koob/ui';
import { answerText, splitAnswer } from './answerParts';
import AnswerMarkdown from './AnswerMarkdown';
import AnswerBlock from './AnswerBlock';
import { findSection, sectionLabel } from './sections';

const REASONS = ['wrong', 'on_page', 'incomplete', 'other'];

function Thinking() {
  const { t } = useTranslation('assistant');
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <span className="inline-flex gap-1" aria-hidden="true">
        {[0, 150, 300].map(delay => (
          <span
            key={delay}
            className="size-1.5 animate-pulse rounded-full bg-gray-400 motion-reduce:animate-none"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </span>
      {t('thinking')}
    </div>
  );
}

function AnswerContent({ message, actions }) {
  const parts = splitAnswer(message.answer, message.status === 'streaming');
  return (
    <div className="flex flex-col gap-3">
      {parts.map((part, i) =>
        part.type === 'markdown' ? (
          <AnswerMarkdown key={i} text={part.text} />
        ) : (
          <AnswerBlock
            key={i}
            {...part}
            fresh={message.status === 'streaming'}
            actions={actions}
          />
        ),
      )}
    </div>
  );
}

function Sources({ sources, onShowSection }) {
  const { t } = useTranslation('assistant');
  if (!sources?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{t('sources')}</span>
      {sources.map(title => {
        const label = sectionLabel(t, title);
        const chip =
          'inline-flex items-center gap-1 rounded-md border border-solid border-gray-200 bg-transparent px-1.5 py-0.5 text-xs text-muted-foreground';
        return findSection(title) ? (
          <button
            key={title}
            type="button"
            onClick={() => onShowSection(title)}
            title={t('showSection')}
            className={`${chip} cursor-pointer hover:border-gray-300 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
          >
            <i className={`${icons.docs} text-[10px]`} aria-hidden="true" />
            {label}
          </button>
        ) : (
          <span key={title} className={chip}>
            <i className={`${icons.docs} text-[10px]`} aria-hidden="true" />
            {label}
          </span>
        );
      })}
    </div>
  );
}

/** Thumbs down asks why, inline: the panel is a dialog, a popover would close it. */
function ReasonPicker({ onPick, onSkip }) {
  const { t } = useTranslation('assistant');
  const [reason, setReason] = useState(null);
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-solid border-gray-200 p-3">
      <span className="text-sm font-medium">{t('reasons.title')}</span>
      <div className="flex flex-wrap gap-1.5">
        {REASONS.map(value => (
          <KoobPill
            key={value}
            active={reason === value}
            aria-pressed={reason === value}
            onClick={() => setReason(value)}
          >
            {t(`reasons.${value}`)}
          </KoobPill>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <KoobButton size="sm" variant="subtle" onClick={onSkip}>
          {t('reasons.skip')}
        </KoobButton>
        <KoobButton
          size="sm"
          variant="black"
          disabled={!reason}
          onClick={() => onPick(reason)}
        >
          {t('reasons.send')}
        </KoobButton>
      </div>
    </div>
  );
}

function Actions({ message, chat }) {
  const { t } = useTranslation('assistant');
  const [copied, setCopied] = useState(false);
  const [asking, setAsking] = useState(false);
  const timer = useRef();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(answerText(message.answer));
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard refused (permissions): nothing else to offer.
    }
  };

  const rate = feedback => {
    chat.rate(message, feedback);
    setAsking(feedback === 'down');
  };

  return (
    <>
      <div className="-ml-1.5 flex items-center gap-0.5">
        <KoobButton
          size="icon"
          variant="subtle"
          aria-label={t(copied ? 'copied' : 'copy')}
          title={t(copied ? 'copied' : 'copy')}
          onClick={copy}
        >
          <i className={copied ? icons.check : icons.copy} />
        </KoobButton>
        {[
          ['up', icons.thumbsUp, t('helpful')],
          ['down', icons.thumbsDown, t('notHelpful')],
        ].map(([value, icon, label]) => (
          <KoobButton
            key={value}
            size="icon"
            variant={message.feedback === value ? 'soft' : 'subtle'}
            aria-label={label}
            aria-pressed={message.feedback === value}
            title={label}
            onClick={() => rate(value)}
          >
            <i className={icon} />
          </KoobButton>
        ))}
      </div>
      {asking && (
        <ReasonPicker
          onPick={reason => {
            chat.rate(message, 'down', reason);
            setAsking(false);
          }}
          onSkip={() => setAsking(false)}
        />
      )}
    </>
  );
}

export default function AssistantMessage({ message, chat, tripId, actions }) {
  const { t } = useTranslation('assistant');
  const done = message.status === 'done';

  let answer;
  if (message.status === 'error') {
    answer = <p className="m-0 text-sm text-red-600">{t(message.error)}</p>;
  } else if (!message.answer) {
    answer = <Thinking />;
  } else if (message.notProvided) {
    answer = (
      <KoobAlert size="sm" variant="warning" icon={icons.info}>
        <div className="flex flex-col items-start gap-2">
          <AnswerContent message={message} actions={actions} />
          {done && tripId && (
            <KoobButton asChild size="sm">
              <a
                href={`/trips/${encodeURIComponent(
                  tripId,
                )}/chat?question=${encodeURIComponent(message.question)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <i className={icons.message} />
                {t('askTheDmc')}
              </a>
            </KoobButton>
          )}
        </div>
      </KoobAlert>
    );
  } else {
    answer = <AnswerContent message={message} actions={actions} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="max-w-[85%] self-end whitespace-pre-line rounded-2xl rounded-br-md bg-gray-100 px-3.5 py-2 text-sm text-gray-900">
        {message.question}
      </div>
      <div className="flex flex-col gap-2.5">
        {answer}
        {done && (
          <>
            <Sources
              sources={message.sources}
              onShowSection={actions.showSection}
            />
            <Actions message={message} chat={chat} />
          </>
        )}
      </div>
    </div>
  );
}
